import type { MetaRow } from "../../lib/ccApi";

/**
 * The Results page's numbers, read from Meta's own insights rows (notes/AUDIENCE.md 7.5): the main ones
 * the page always shows, and every other field Meta sent, flattened so the owner can add any of them.
 * Hook rate and hold rate are worked out from Meta's numbers and say so.
 */

type Action = { action_type?: string; value?: string };

const asNumber = (v: unknown): number | null => {
  const n = typeof v === "number" ? v : typeof v === "string" ? Number(v) : NaN;
  return Number.isFinite(n) ? n : null;
};

/** A field's number, or one action type's number inside a list of actions. */
export function num(row: MetaRow | null, field: string, actionType?: string): number | null {
  const v = row?.[field];
  if (Array.isArray(v)) {
    const hit = (v as Action[]).find((a) => (actionType ? a.action_type === actionType : true));
    return hit ? asNumber(hit.value) : null;
  }
  return asNumber(v);
}

/** Meta's own "Results": a list of indicators, each with its values. */
export function results(row: MetaRow | null): number | null {
  const v = row?.results;
  if (!Array.isArray(v)) return null;
  return (v as { values?: { value?: string }[]; value?: string }[]).reduce((n, r) => n + (asNumber(Array.isArray(r.values) ? r.values[0]?.value : r.value) ?? 0), 0);
}

const firstOf = (row: MetaRow | null, field: string, types: string[]) => {
  for (const t of types) {
    const n = num(row, field, t);
    if (n !== null) return n;
  }
  return null;
};

const PURCHASE = ["omni_purchase", "purchase", "offsite_conversion.fb_pixel_purchase", "onsite_web_purchase"];

/** The numbers every ad card and the ads together always show. */
export interface Main {
  spend: number | null;
  results: number | null;
  costEach: number | null;
  impressions: number | null;
  reach: number | null;
  frequency: number | null;
  cpm: number | null;
  linkClicks: number | null;
  ctr: number | null;
  cpc: number | null;
  pageViews: number | null;
  plays: number | null;
  hook: number | null;
  thruplays: number | null;
  hold: number | null;
  watch: number | null;
  purchases: number | null;
  value: number | null;
  roas: number | null;
  created: string | null;
}

export function main(row: MetaRow | null): Main {
  const spend = num(row, "spend");
  const res = results(row);
  const cpr = num(row, "cost_per_result");
  const impressions = num(row, "impressions");
  const plays = num(row, "actions", "video_view");
  const thruplays = num(row, "video_thruplay_watched_actions", "video_view");
  const purchases = firstOf(row, "actions", PURCHASE);
  const value = firstOf(row, "action_values", PURCHASE);
  const roas = firstOf(row, "purchase_roas", PURCHASE) ?? (value !== null && spend ? value / spend : null);
  return {
    spend,
    results: res,
    costEach: cpr ?? (spend !== null && res ? spend / res : null),
    impressions,
    reach: num(row, "reach"),
    frequency: num(row, "frequency"),
    cpm: num(row, "cpm"),
    linkClicks: num(row, "inline_link_clicks"),
    ctr: num(row, "inline_link_click_ctr"),
    cpc: num(row, "cost_per_inline_link_click"),
    pageViews: num(row, "actions", "landing_page_view"),
    plays,
    hook: plays !== null && impressions ? plays / impressions : null,
    thruplays,
    hold: thruplays !== null && plays ? thruplays / plays : null,
    watch: num(row, "video_avg_time_watched_actions", "video_view"),
    purchases,
    value,
    roas,
    created: typeof row?.created_time === "string" ? row.created_time : null,
  };
}

// --- Words ------------------------------------------------------------------------------------------------

/** Meta's ranking values, in plain words. */
export const RANKING: Record<string, string> = {
  ABOVE_AVERAGE: "Above average",
  AVERAGE: "Average",
  BELOW_AVERAGE_35: "Below average (bottom 35%)",
  BELOW_AVERAGE_20: "Below average (bottom 20%)",
  BELOW_AVERAGE_10: "Below average (bottom 10%)",
  UNKNOWN: "Not enough to rank",
};

/** Plain names for Meta's fields; any other field shows under its own name. */
const FIELD_NAMES: Record<string, string> = {
  spend: "Spent",
  impressions: "Times shown",
  reach: "People reached",
  frequency: "Times each person saw it",
  cpm: "Cost per 1,000 views",
  cpp: "Cost per 1,000 people reached",
  clicks: "All clicks",
  ctr: "Click rate (all clicks)",
  cpc: "Cost per click (all clicks)",
  unique_clicks: "People who clicked",
  unique_ctr: "Click rate, one per person",
  inline_link_clicks: "Link clicks",
  inline_link_click_ctr: "Click rate",
  cost_per_inline_link_click: "Cost per click",
  unique_inline_link_clicks: "People who clicked a link",
  unique_link_clicks_ctr: "Link click rate, one per person",
  outbound_clicks: "Clicks off Meta",
  outbound_clicks_ctr: "Click rate off Meta",
  cost_per_outbound_click: "Cost per click off Meta",
  inline_post_engagement: "Post engagement",
  cost_per_inline_post_engagement: "Cost per engagement",
  social_spend: "Spent on ads showing friends' likes",
  quality_ranking: "Quality ranking",
  engagement_rate_ranking: "Engagement rate ranking",
  conversion_rate_ranking: "Conversion rate ranking",
  cost_per_thruplay: "Cost per 15-second view",
  video_thruplay_watched_actions: "Watched 15 seconds",
  video_play_actions: "Video plays",
  video_avg_time_watched_actions: "Average watch time",
  video_p25_watched_actions: "Watched 25%",
  video_p50_watched_actions: "Watched 50%",
  video_p75_watched_actions: "Watched 75%",
  video_p95_watched_actions: "Watched 95%",
  video_p100_watched_actions: "Watched 100%",
  video_continuous_2_sec_watched_actions: "2-second continuous plays",
  video_15_sec_watched_actions: "15-second plays",
  video_30_sec_watched_actions: "30-second plays",
  purchase_roas: "Sales per $1 spent",
  website_purchase_roas: "Website sales per $1 spent",
  results: "Results",
  cost_per_result: "Cost per result",
  actions: "Actions",
  action_values: "Action values",
  cost_per_action_type: "Cost per action",
  conversions: "Conversions",
  cost_per_conversion: "Cost per conversion",
  estimated_ad_recallers: "Estimated ad recall lift (people)",
  estimated_ad_recall_rate: "Estimated ad recall lift (rate)",
  full_view_impressions: "Full views",
  full_view_reach: "Full-view reach",
  instant_experience_clicks_to_open: "Full-screen ad opens",
};

/** Plain names for Meta's action types. */
const ACTION_NAMES: Record<string, string> = {
  lead: "Sign-ups (all)",
  "onsite_conversion.lead_grouped": "Sign-ups on Meta's form",
  "offsite_conversion.fb_pixel_lead": "Sign-ups on your page",
  landing_page_view: "Visits to your page",
  link_click: "Link clicks",
  video_view: "3-second plays",
  post_engagement: "Post engagement",
  page_engagement: "Page engagement",
  post_reaction: "Reactions",
  comment: "Comments",
  post: "Shares",
  "onsite_conversion.post_save": "Saves",
  purchase: "Purchases",
  omni_purchase: "Purchases",
  "offsite_conversion.fb_pixel_purchase": "Website purchases",
  complete_registration: "Registrations",
  "offsite_conversion.fb_pixel_complete_registration": "Registrations on your page",
  schedule: "Schedules",
  contact: "Contacts",
  view_content: "Content views",
  "offsite_conversion.fb_pixel_view_content": "Website content views",
  "offsite_conversion.fb_pixel_custom": "Your own events",
};

const humanize = (s: string) => {
  const t = s.replace(/^offsite_conversion\.fb_pixel_/, "website ").replace(/^onsite_conversion\./, "").replace(/[._]/g, " ").trim();
  return t.charAt(0).toUpperCase() + t.slice(1);
};
export const fieldName = (field: string) => FIELD_NAMES[field] ?? humanize(field);
export const actionName = (type: string) => ACTION_NAMES[type] ?? humanize(type);

/** Fields that name the row rather than count anything. */
const NOT_NUMBERS = new Set(["account_id", "account_name", "account_currency", "ad_id", "ad_name", "adset_id", "adset_name", "campaign_id", "campaign_name", "date_start", "date_stop", "created_time", "updated_time", "objective", "optimization_goal", "buying_type", "attribution_setting"]);

/** The numbers the page always shows, so "Add a number" offers only the others. */
export const SHOWN = new Set([
  "spend", "impressions", "reach", "frequency", "cpm", "inline_link_clicks", "inline_link_click_ctr", "cost_per_inline_link_click",
  "actions:landing_page_view", "actions:video_view", "video_thruplay_watched_actions:video_view", "video_avg_time_watched_actions:video_view",
  "quality_ranking", "engagement_rate_ranking", "conversion_rate_ranking", "cost_per_result",
]);

/** One number Meta sent: a field (`spend`), or one action type inside a list (`actions:video_view`). */
export interface Field {
  key: string;
  group: string;
  name: string;
  meta: string;
  value: unknown;
  /** "money" for amounts in the ad account's currency, "rate" for percentages, "ranking" for Meta's rankings. */
  kind: "count" | "money" | "rate" | "ranking" | "text" | "seconds";
}

const kindOf = (field: string): Field["kind"] => {
  if (field.endsWith("_ranking")) return "ranking";
  if (/(^|_)(spend|cpm|cpc|cpp|value|values|cost|bid)(_|$)/.test(field) && !/roas/.test(field)) return "money";
  if (/ctr|rate|ranking/.test(field)) return "rate";
  if (field === "video_avg_time_watched_actions") return "seconds";
  return "count";
};

const groupOf = (field: string) =>
  /ranking|recall|quality/.test(field)
    ? "Quality"
    : /video|thruplay/.test(field)
    ? "Video"
    : /click|ctr|cpc|outbound|landing/.test(field)
      ? "Clicks"
      : /roas|purchase|value|conversion|result|action|lead/.test(field)
        ? "Results and sales"
        : /engagement|reaction|comment|post|follow|profile/.test(field)
          ? "Engagement"
          : "Delivery";

/** Every number in a row: each plain field, and each action type inside each list of actions. */
export function everyField(row: MetaRow | null): Field[] {
  if (!row) return [];
  const out: Field[] = [];
  for (const [k, v] of Object.entries(row)) {
    if (NOT_NUMBERS.has(k) || v === null || v === undefined) continue;
    if (Array.isArray(v)) {
      for (const a of v as Action[]) {
        if (!a || typeof a !== "object" || !a.action_type) continue;
        out.push({ key: `${k}:${a.action_type}`, group: groupOf(k), name: `${fieldName(k)}: ${actionName(a.action_type)}`, meta: `${k} · ${a.action_type}`, value: a.value, kind: kindOf(k) === "count" && k.startsWith("cost_per") ? "money" : kindOf(k) });
      }
    } else if (typeof v === "string" || typeof v === "number") {
      out.push({ key: k, group: groupOf(k), name: fieldName(k), meta: k, value: v, kind: asNumber(v) === null ? (k.endsWith("_ranking") ? "ranking" : "text") : kindOf(k) });
    }
  }
  return out.toSorted((a, b) => a.group.localeCompare(b.group) || a.name.localeCompare(b.name));
}

/** One added number's value in a row, by its key. */
export function fieldValue(row: MetaRow | null, key: string): unknown {
  const [field, type] = key.split(":");
  const v = row?.[field];
  if (type) return Array.isArray(v) ? (v as Action[]).find((a) => a.action_type === type)?.value : undefined;
  return v;
}

// --- Formatting -------------------------------------------------------------------------------------------

const count = (n: number) => new Intl.NumberFormat(undefined, { maximumFractionDigits: n < 10 && n % 1 ? 1 : 0 }).format(n);

export function formatter(currency: string | null) {
  const money = (n: number) => {
    try {
      return new Intl.NumberFormat(undefined, { style: "currency", currency: currency || "USD", maximumFractionDigits: n < 100 ? 2 : 0 }).format(n);
    } catch {
      return n.toFixed(2);
    }
  };
  return {
    money: (n: number | null) => (n === null ? "none" : money(n)),
    count: (n: number | null) => (n === null ? "none" : count(n)),
    pct: (n: number | null, digits = 0) => (n === null ? "none" : `${(n * 100).toFixed(digits)}%`),
    /** Meta's own percentages (CTR) already come as percent. */
    metaPct: (n: number | null) => (n === null ? "none" : `${n.toFixed(2)}%`),
    ratio: (n: number | null) => (n === null ? "none" : n.toFixed(n < 10 ? 2 : 1)),
    seconds: (n: number | null) => (n === null ? "none" : `${Math.round(n)} s`),
    field: (f: Pick<Field, "kind">, v: unknown) => {
      if (v === undefined || v === null || v === "") return "none";
      const n = asNumber(v);
      if (f.kind === "ranking") return RANKING[String(v)] ?? String(v);
      if (n === null) return String(v);
      if (f.kind === "money") return money(n);
      if (f.kind === "rate") return `${n.toFixed(2)}%`;
      if (f.kind === "seconds") return `${Math.round(n)} s`;
      return count(n);
    },
  };
}
